/**
 * Web Crypto API Encryption Helper
 * Provides real cryptographic encryption using AES-GCM algorithm
 *
 * SECURITY FEATURES:
 * - AES-GCM 256-bit encryption (industry standard)
 * - Unique encryption key derived from browser fingerprint
 * - Random IV (Initialization Vector) for each encryption
 * - Authenticated encryption (prevents tampering)
 *
 * LIMITATIONS:
 * - Still stored in localStorage (vulnerable to XSS)
 * - For maximum security, use httpOnly cookies (requires backend support)
 */

class CryptoHelper {
  private static instance: CryptoHelper;
  private cryptoKey: CryptoKey | null = null;
  private readonly ALGORITHM = 'AES-GCM';
  private readonly KEY_LENGTH = 256;
  private readonly IV_LENGTH = 12; // 12 bytes for AES-GCM

  private constructor() {
    // Private constructor for singleton
  }

  static getInstance(): CryptoHelper {
    if (!CryptoHelper.instance) {
      CryptoHelper.instance = new CryptoHelper();
    }
    return CryptoHelper.instance;
  }

  /**
   * Generate a browser fingerprint for key derivation
   * Uses multiple browser characteristics to create a unique identifier
   */
  private async getBrowserFingerprint(): Promise<string> {
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      new Date().getTimezoneOffset().toString(),
      window.screen.colorDepth.toString(),
      window.screen.width.toString() + 'x' + window.screen.height.toString(),
      // Add a stable app identifier
      'pms-customer-frontend-v1'
    ].join('|');

    return fingerprint;
  }

  /**
   * Derive a cryptographic key from browser fingerprint using PBKDF2
   * This ensures each browser has a unique encryption key
   */
  private async deriveKey(): Promise<CryptoKey> {
    if (this.cryptoKey) {
      return this.cryptoKey;
    }

    try {
      const fingerprint = await this.getBrowserFingerprint();
      const encoder = new TextEncoder();
      const fingerprintBuffer = encoder.encode(fingerprint);

      // Import the fingerprint as key material
      const keyMaterial = await window.crypto.subtle.importKey(
        'raw',
        fingerprintBuffer,
        'PBKDF2',
        false,
        ['deriveBits', 'deriveKey']
      );

      // Use a fixed salt (in production, you might want to store this securely)
      const salt = encoder.encode('pms-secure-salt-2025');

      // Derive the actual encryption key
      this.cryptoKey = await window.crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: salt,
          iterations: 100000,
          hash: 'SHA-256'
        },
        keyMaterial,
        {
          name: this.ALGORITHM,
          length: this.KEY_LENGTH
        },
        false,
        ['encrypt', 'decrypt']
      );

      return this.cryptoKey;
    } catch (error) {
      console.error('Failed to derive encryption key:', error);
      throw new Error('Encryption key derivation failed');
    }
  }

  /**
   * Encrypt data using AES-GCM
   * Returns: base64(iv + encryptedData)
   */
  async encrypt(plaintext: string): Promise<string> {
    try {
      const key = await this.deriveKey();
      const encoder = new TextEncoder();
      const data = encoder.encode(plaintext);

      // Generate random IV
      const iv = window.crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));

      // Encrypt the data
      const encryptedBuffer = await window.crypto.subtle.encrypt(
        {
          name: this.ALGORITHM,
          iv: iv
        },
        key,
        data
      );

      // Combine IV + encrypted data
      const encryptedArray = new Uint8Array(encryptedBuffer);
      const combined = new Uint8Array(iv.length + encryptedArray.length);
      combined.set(iv, 0);
      combined.set(encryptedArray, iv.length);

      // Convert to base64 for storage
      return this.arrayBufferToBase64(combined);
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Encryption failed');
    }
  }

  /**
   * Decrypt data using AES-GCM
   * Expects: base64(iv + encryptedData)
   */
  async decrypt(ciphertext: string): Promise<string> {
    try {
      const key = await this.deriveKey();

      // Convert from base64
      const combined = this.base64ToArrayBuffer(ciphertext);

      // Extract IV and encrypted data
      const iv = combined.slice(0, this.IV_LENGTH);
      const encryptedData = combined.slice(this.IV_LENGTH);

      // Decrypt
      const decryptedBuffer = await window.crypto.subtle.decrypt(
        {
          name: this.ALGORITHM,
          iv: iv
        },
        key,
        encryptedData
      );

      // Convert back to string
      const decoder = new TextDecoder();
      return decoder.decode(decryptedBuffer);
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Decryption failed');
    }
  }

  /**
   * Check if Web Crypto API is available
   */
  isAvailable(): boolean {
    return !!(window.crypto && window.crypto.subtle);
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: Uint8Array): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Convert base64 string to ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Detect if data is encrypted with the old base64 method
   * Old format: base64("pms-secure-key:data")
   */
  isLegacyEncryption(data: string): boolean {
    try {
      const decoded = atob(data);
      return decoded.startsWith('pms-secure-key:');
    } catch {
      return false;
    }
  }

  /**
   * Decrypt legacy base64-encoded data
   */
  decryptLegacy(data: string): string {
    try {
      const decoded = atob(data);
      if (decoded.startsWith('pms-secure-key:')) {
        return decoded.substring('pms-secure-key:'.length);
      }
      return decoded;
    } catch (error) {
      throw new Error('Legacy decryption failed');
    }
  }
}

export default CryptoHelper.getInstance();